"use client";

import { useEffect, useState } from "react";
import { onSnapshot } from "firebase/firestore";
import { getDb } from "@/lib/firebase/client";
import { paths, refCol } from "@/lib/db/paths";
import { moneyTalkSchema, type MoneyTalk } from "@/lib/schemas";

type State = { talks: MoneyTalk[]; loading: boolean; error: string | null };

const cache = new Map<string, MoneyTalk[]>();

/**
 * Odbyte rozmowy Money Talk całego HH. Kolekcja jest mała (max 52 doc/rok),
 * więc słuchamy całości bez filtrów — wzorzec useGoals.
 */
export function useMoneyTalks(householdId: string | undefined): State {
  const cached = householdId ? cache.get(householdId) : undefined;
  const [state, setState] = useState<State>({
    talks: cached ?? [],
    loading: cached === undefined,
    error: null,
  });

  useEffect(() => {
    if (!householdId) return;
    const db = getDb();
    const unsub = onSnapshot(
      refCol(db, paths.moneyTalks(householdId)),
      (snap) => {
        const talks = snap.docs
          .flatMap((d) => {
            const parsed = moneyTalkSchema.safeParse(d.data());
            return parsed.success ? [parsed.data] : [];
          })
          .sort((a, b) => b.weekKey.localeCompare(a.weekKey));
        cache.set(householdId, talks);
        setState({ talks, loading: false, error: null });
      },
      (err) => setState({ talks: [], loading: false, error: err.message }),
    );
    return () => unsub();
  }, [householdId]);

  return state;
}
